import { isDobValid, isEmailValid, isNameValid } from "universal/aphValidators";
import AphButton from "components/web-ui-components/AphButton";
import AphDialog from "components/web-ui-components/AphDialog";
import AphDialogClose from "components/web-ui-components/AphDialogClose";
import AphDialogTitle from "components/web-ui-components/AphDialogTitle";
import AphSelect from "components/web-ui-components/AphSelect";
import AphTextField from "components/web-ui-components/AphTextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import { Alerts } from "components/Alerts/Alerts";
import { AddNewProfileConfirm } from "components/MyAccount/AddNewProfileConfirm";
import { ADD_PROFILE, EDIT_PROFILE } from "graphql/profiles";
import { ConsultMode, Gender, Relation } from "graphql/types/globalTypes";
import {
  getPatientAttributes,
  getPatientDetails,
  INVALID_FILE_SIZE_ERROR,
  MAX_FILE_SIZE_FOR_UPLOAD,
  oneTapEventDetails,
  toBase64,
  uploadFileToStorage,
} from "helpers/commonHelpers";
import { useRefetchPatients, useAuth } from "hooks/authHooks";
import _find from "lodash/find";
import _startCase from "lodash/startCase";
import _toLower from "lodash/toLower";
import React, { useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation } from "react-apollo-hooks";
import parse from "date-fns/parse";
import format from "date-fns/format";
import differenceInYears from "date-fns/differenceInYears";
import _get from "lodash/get";
import clevertapTracking, { eventTracking } from "../../../clevertapTracking";
import { dataLayerTracking } from "gtmTracking";
import { GetPatientByMobileNumber_getPatientByMobileNumber_patients as patientDetails } from "graphql/types/GetPatientByMobileNumber";
import { getValidDateInput, toWebFormat } from "helpers/dateHelpers";
import { MINIMUM_AGE_FOR_DIAGNOSTIC_ORDER } from "strings/AppConfig";
import styles from "./AddNewProfile.module.scss";
import { GetDoctorDetailsById_getDoctorDetailsById } from "graphql/types/GetDoctorDetailsById";
import { PLATFORM_TYPE } from "helpers/constants";
import useMobileDetect from "helpers/useMobileDetect";

interface AddNewProfileProps {
  closeHandler: (popOpen: boolean) => void;
  selectedPatientId: string;
  successHandler: (isPopoverOpen: boolean) => void;
  isProfileDelete: boolean;
  isMeClicked: boolean;
  isEdit?: boolean;
  addNewProfileFromEditMenu?: () => void;
  setNewPatientId?: (newPatientId: string) => void;
  isProHealth?: boolean;
  refetchProfiles?: (newProfile: UserInputInterface) => void;
  updateProfileFromProHealth?: (newProfile: UserInputInterface) => void;
  isRequestAppointmentDialog?: boolean;
  disableMinorPatient?: boolean;
  patientGender?: string;
  source?: string;
  type?: string;
  displayId?: number;
  isDiagnosticOrder?: boolean;
  doctorDetails?: GetDoctorDetailsById_getDoctorDetailsById;
  consultMode?: ConsultMode;
  isCheckoutPage?: boolean;
  facilityId?: string;
}

export interface UserInputInterface {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  relation: string;
  emailAddress: string;
  photoUrl: string;
  id: string;
  mobileNumber: string;
  facilityId?: string;
}

export const AddNewProfile: React.FC<AddNewProfileProps> = (props) => {
  const {
    closeHandler,
    selectedPatientId,
    successHandler,
    isProfileDelete,
    setNewPatientId,
    isProHealth,
    refetchProfiles,
    updateProfileFromProHealth,
    isRequestAppointmentDialog,
    patientGender,
    disableMinorPatient,
    type,
    source,
    displayId,
    isEdit,
    doctorDetails,
    consultMode,
    isCheckoutPage,
  } = props;
  const { isMobile } = useMobileDetect();
  const disableFieldsRef = useRef(null);
  const [mutationLoading, setMutationLoading] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [genderSelected, setGenderSelected] = useState<string>("");
  const [relation, setRelation] = useState<string>("");
  const [selectedRelation, setSelectedRelation] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [photoUrl, setPhotoUrl] = useState<string>("");

  const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(true);
  const [isLastNameValid, setIsLastNameValid] = useState<boolean>(true);
  const [isRelationValid, setIsRelationValid] = useState<boolean>(true);
  const [isGenderValid, setIsGenderValid] = useState<boolean>(true);
  const [isValidDob, setIsValidDob] = useState<boolean>(true);
  const [isEmailAddressValid, setIsEmailAddressValid] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [alertMessage, setAlertMessage] = React.useState<string>("");
  const [isAlertOpen, setIsAlertOpen] = React.useState<boolean>(false);
  const [
    isAddNewProfileConfirmDialogOpen,
    setIsAddNewProfileConfirmDialogOpen,
  ] = React.useState<boolean>(false);
  const [userInput, setUserInput] = React.useState<UserInputInterface>(null);
  const { allCurrentPatients, currentPatient } = useAuth();
  const apolloClient = useApolloClient();
  const { refetchPatients } = useRefetchPatients();
  const orderedGenders = [Gender.MALE, Gender.FEMALE, Gender.OTHER];
  const patientDetails = getPatientDetails(currentPatient);
  const [
    selectedPatientDetails,
    setSelectedPatientDetails,
  ] = useState<patientDetails | null>(null);
  const orderedRelations = [
    Relation.BROTHER,
    Relation.COUSIN,
    Relation.DAUGHTER,
    Relation.FATHER,
    Relation.GRANDDAUGHTER,
    Relation.GRANDFATHER,
    Relation.GRANDMOTHER,
    Relation.GRANDSON,
    Relation.HUSBAND,
    Relation.ME,
    Relation.MOTHER,
    Relation.OTHER,
    Relation.SISTER,
    Relation.SON,
    Relation.WIFE,
  ];

  const [addProfileMutation] = useMutation(ADD_PROFILE);
  const [editProfileMutation] = useMutation(EDIT_PROFILE);

  const multiplePrimaryUsers =
    allCurrentPatients &&
    allCurrentPatients.filter((x) => x.relation === Relation.ME).length > 0;
  const [
    primaryUserErrorMessage,
    setPrimaryUserErrorMessage,
  ] = useState<string>("");
  const [
    dateOfBirthErrorMessage,
    setDateofBirthErrorMessage,
  ] = useState<string>("");
  const [isDisablePopoverOpen, setIsDisablePopoverOpen] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (patientGender?.length > 0) {
      setGenderSelected(patientGender);
    }
  }, [patientGender]);

  useEffect(() => {
    // if (selectedPatientId.length > 0) {
    const selectedPatientDetails = _find(
      allCurrentPatients,
      (currentPatientDetails) => {
        return currentPatientDetails.id === selectedPatientId;
        // return currentPatientDetails.relation === "ME";
      }
    );

    if (
      multiplePrimaryUsers &&
      selectedRelation === "ME" &&
      selectedPatientDetails &&
      selectedPatientDetails.relation !== Relation.ME
    ) {
      setPrimaryUserErrorMessage(
        "Relation can be set as Me for only 1 profile"
      );
    } else {
      setPrimaryUserErrorMessage("");
    }
    if (
      selectedRelation === Relation.ME &&
      selectedPatientDetails?.relation !== "ME" &&
      multiplePrimaryUsers
    ) {
      setPrimaryUserErrorMessage(
        "Relation can be set as Me for only 1 profile"
      );
    } else {
      setPrimaryUserErrorMessage("");
    }
  }, [selectedRelation, selectedPatientId]);

  const patientDetailsChanged = () => {
    return (
      toWebFormat(selectedPatientDetails?.dateOfBirth) !== dob ||
      selectedPatientDetails?.emailAddress !== emailAddress ||
      selectedPatientDetails?.relation !== relation ||
      selectedPatientDetails?.firstName !== firstName ||
      selectedPatientDetails?.lastName !== lastName ||
      selectedPatientDetails?.gender !== genderSelected
    );
  };

  useEffect(() => {
    if (isValidDob) {
      if (disableMinorPatient) {
        const patientAge = differenceInYears(
          new Date(),
          new Date(getValidDateInput(dob))
        );
        if (patientAge < MINIMUM_AGE_FOR_DIAGNOSTIC_ORDER) {
          setDateofBirthErrorMessage(
            "We allow only RTPCR bookings for children below 10 years"
          );
        } else {
          setIsValidDob(true);
          setDateofBirthErrorMessage("");
        }
      } else {
        setIsValidDob(true);
        setDateofBirthErrorMessage("");
      }
    } else {
      setDateofBirthErrorMessage("Invalid date of birth");
    }
  }, [dob, isValidDob, disableMinorPatient]);

  useEffect(() => {
    if (selectedPatientId.length > 0) {
      const selectedPatientDetails = _find(
        allCurrentPatients,
        (currentPatientDetails) => {
          return currentPatientDetails.id === selectedPatientId;
        }
      );
      if (selectedPatientDetails) {
        setSelectedPatientDetails(selectedPatientDetails);
      }
      if (selectedPatientDetails && isEdit) {
        setRelation(
          selectedPatientDetails && selectedPatientDetails.relation
            ? selectedPatientDetails.relation
            : ""
        );
        setSelectedRelation(selectedPatientDetails?.relation ?? "");
        setGenderSelected(
          selectedPatientDetails && selectedPatientDetails.gender
            ? selectedPatientDetails.gender
            : ""
        );
        setFirstName(
          selectedPatientDetails && selectedPatientDetails.firstName
            ? selectedPatientDetails.firstName
            : ""
        );
        setLastName(
          selectedPatientDetails && selectedPatientDetails.lastName
            ? selectedPatientDetails.lastName
            : ""
        );
        setDob(
          selectedPatientDetails && selectedPatientDetails.dateOfBirth
            ? format(new Date(selectedPatientDetails.dateOfBirth), "dd/MM/yyyy")
            : ""
        );
        setEmailAddress(
          selectedPatientDetails && selectedPatientDetails.emailAddress
            ? selectedPatientDetails.emailAddress
            : ""
        );
        setPhotoUrl(
          selectedPatientDetails && selectedPatientDetails.photoUrl
            ? selectedPatientDetails.photoUrl
            : `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/no_photo.png`
        );
      }
    }
  }, [allCurrentPatients, selectedPatientId]);

  const disableSubmitButton =
    (firstName.length > 0 &&
      lastName.length > 0 &&
      dob.length > 0 &&
      genderSelected.length > 0 &&
      relation.length > 0 &&
      (emailAddress !== "" ? emailAddress.length > 0 : true) &&
      isFirstNameValid &&
      isLastNameValid &&
      isRelationValid &&
      isGenderValid &&
      isValidDob &&
      (emailAddress !== "" ? isEmailAddressValid : true) &&
      (selectedPatientId !== "" ? patientDetailsChanged() : true) &&
      !primaryUserErrorMessage.length &&
      !dateOfBirthErrorMessage.length) ||
    isProfileDelete;

  const handleProfileSubmit = () => {
    setIsAddNewProfileConfirmDialogOpen(false);
    setMutationLoading(true);
    let userObject = userInput;
    userObject.facilityId = props?.facilityId;
    delete userObject.id;
    addProfileMutation({
      variables: {
        PatientProfileInput: { ...userObject },
      },
    })
      .then((response: any) => {
        // this any is forced to be written - 13102020
        if (selectedPatientId.length > 0 || displayId) {
          diagnosticOrderEvent();
        } else {
          eventTracking("Confirm Member Profile Clicked", {
            ...getPatientAttributes(patientDetails),
            Nav_src: "Home page",
          });
        }
        if (setNewPatientId) {
          let list = response && response.data ? response.data : [];
          const profileId =
            _get(list, list.addNewProfile, list.addNewProfile.patient.id) || "";
          setNewPatientId(profileId);
          if (isCheckoutPage) {
            const eventData = {
              ...oneTapEventDetails(currentPatient),
              "Doctor ID": doctorDetails?.id,
              "Doctor Name": doctorDetails?.displayName,
              "Doctor hospital":
                doctorDetails?.doctorHospital?.[0]?.facility?.name || "",
              "Doctor type": doctorDetails?.doctorType,
              "Speciality name": doctorDetails?.specialty?.name,
              "Consult Type": consultMode,
              "Doctor Fees":
                consultMode === ConsultMode.ONLINE
                  ? doctorDetails?.onlineConsultationFees
                  : doctorDetails?.physicalConsultationFees,
              "Number of Profiles": allCurrentPatients?.length,
              Platform: isMobile ? PLATFORM_TYPE.mWeb : PLATFORM_TYPE.Web,
              "Patient ID created": profileId,
              "UHID created": list?.addNewProfile?.patient?.uhid || "",
            };
            eventTracking("New Member Added", eventData);
          }
          if (isProHealth) {
            let newProfile =
              _get(list, list.addNewProfile, list.addNewProfile.patient) || "";
            refetchProfiles(newProfile);
          }
        }
        refetchPatients();
        closeHandler(false);
        successHandler(true);
        dataLayerTracking({
          event: "new_profile_added",
          uhid: currentPatient?.uhid,
          email: emailAddress,
          section: "My Account",
          lob: "PHR",
          gender: genderSelected,
        });
      })
      .catch((e) => {
        setIsAlertOpen(true);
        setAlertMessage("An error occurred while creating profile.");
        console.error("An error occurred while creating profile.", e);
      })
      .finally(() => {
        setMutationLoading(false);
      });
  };

  const diagnosticOrderProfileCreation = (userObject: any) => {
    delete userObject.id;
    addProfileMutation({
      variables: {
        PatientProfileInput: { ...userObject },
      },
    })
      .then(({ data }: any) => {
        if (setNewPatientId) {
          setNewPatientId(data?.addNewProfile?.patient?.id);
        }
        console.log("updated patient", data);
        diagnosticOrderEvent();
        closeHandler(false);
        successHandler(true);
        if (isProHealth) {
          updateProfileFromProHealth(userObject);
        }
        refetchPatients();
      })
      .catch((e) => {
        setMutationLoading(false);
        setIsAlertOpen(true);
        setAlertMessage("An error occurred while creating profile.");
      });
  };

  const diagnosticOrderEvent = () => {
    clevertapTracking("Diagnostic edit patient details", {
      displayId: displayId ?? "",
      source: source ?? "",
      type: type ?? "",
      oldPatient: {
        name: `${selectedPatientDetails?.firstName} ${selectedPatientDetails?.lastName}`,
        gender: selectedPatientDetails.gender,
      },
      newPatient: {
        name: `${firstName} ${lastName}`,
        gender: genderSelected,
      },
    });
  };

  return (
    <div className={styles.newProfileContainer}>
      <div className={styles.dialogContent}>
        <h6 className={styles.warningMsg}>
          {selectedPatientId.length > 0
            ? props.isDiagnosticOrder
              ? `The patient details for this order would be updated.
                  Previous selected patient details would still be visible in your patient list.`
              : isEdit && !props.isDiagnosticOrder
              ? "You cannot edit Name, Date of Birth and Gender!"
              : "Important : You will not be able to edit these details once you have saved them!"
            : ""}
        </h6>
        <div className={styles.profileForm}>
          <div className={styles.selectImage} title={"Upload Profile"}>
            <input
              accept="image/*"
              className={styles.fileInput}
              id="upload-profile-photo"
              type="file"
              onChange={async (e) => {
                setIsUploading(false);
                const fileNames = e.target.files;
                if (fileNames && fileNames.length > 0) {
                  const file = fileNames[0] || null;
                  const fileExtension = file?.name
                    ?.split(".")
                    ?.pop()
                    ?.toLowerCase();
                  const fileSize = file.size;
                  if (fileSize > MAX_FILE_SIZE_FOR_UPLOAD) {
                    setIsAlertOpen(true);
                    setAlertMessage(INVALID_FILE_SIZE_ERROR);
                  } else if (
                    fileExtension &&
                    ["png", "jpg", "jpeg"].includes(fileExtension)
                  ) {
                    setIsUploading(true);
                    if (file) {
                      try {
                        const base64Response = await (toBase64(
                          file
                        ) as Promise<string>);
                        const content = base64Response
                          ?.split(`;base64,`)
                          ?.pop();
                        const url = await uploadFileToStorage(
                          apolloClient,
                          fileExtension,
                          content
                        );
                        if (url?.length) {
                          setPhotoUrl(url);
                        } else {
                          setAlertMessage(
                            "Unable to upload image, please try again."
                          );
                        }
                      } catch (e) {
                        console.error("Error uploading profile pic", e);
                      }
                    }
                  } else {
                    setIsAlertOpen(true);
                    setAlertMessage(
                      "Invalid File Extension. Only files with .jpg and.png extensions are allowed."
                    );
                  }
                  setIsUploading(false);
                }
              }}
            />
            <label
              className={styles.profileCircle}
              htmlFor="upload-profile-photo"
            >
              {isUploading ? <CircularProgress /> : <img src={photoUrl} />}
            </label>
            <label className={styles.editBtn} htmlFor="upload-profile-photo">
              <img
                src={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic-edit-white.svg`}
              />
            </label>
          </div>
          <div>
            {/* <div className={styles.disabledFields} ref={disableFieldsRef}>
                    </div> */}
            <FormControl
              className={`${styles.formControl} ${styles.noMargin}`}
              ref={disableFieldsRef}
              fullWidth
              onClick={
                selectedPatientId.length && !props.isDiagnosticOrder
                  ? () => setIsDisablePopoverOpen(true)
                  : () => {}
              }
            >
              <AphTextField
                label="Full Name"
                placeholder="First Name"
                inputProps={{ maxLength: 20 }}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
                onBlur={(e) => {
                  setIsFirstNameValid(isNameValid(e.target.value));
                }}
                error={!isFirstNameValid}
                disabled={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                }
                className={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                    ? styles.disabledInput
                    : ""
                }
              />
              {!isFirstNameValid ? (
                <FormHelperText
                  className={
                    !isFirstNameValid ? styles.showMessage : styles.hideMessage
                  }
                  component="div"
                  error={true}
                >
                  Invalid first name
                </FormHelperText>
              ) : null}
            </FormControl>
            <FormControl
              className={`${styles.formControl}`}
              fullWidth
              onClick={
                selectedPatientId.length && !props.isDiagnosticOrder
                  ? () => setIsDisablePopoverOpen(true)
                  : () => {}
              }
            >
              <AphTextField
                placeholder="Last name"
                inputProps={{ maxLength: 20 }}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
                onBlur={(e) => {
                  setIsLastNameValid(isNameValid(e.target.value));
                }}
                error={!isLastNameValid}
                disabled={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                }
                className={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                    ? styles.disabledInput
                    : ""
                }
              />
              {!isLastNameValid ? (
                <FormHelperText
                  className={
                    !isLastNameValid ? styles.showMessage : styles.hideMessage
                  }
                  component="div"
                  error={true}
                >
                  Invalid last name
                </FormHelperText>
              ) : null}
            </FormControl>
            <FormControl
              className={styles.formControl}
              fullWidth
              onClick={
                selectedPatientId.length && !props.isDiagnosticOrder
                  ? () => setIsDisablePopoverOpen(true)
                  : () => {}
              }
            >
              <AphTextField
                label="Date Of Birth"
                placeholder="dd/mm/yyyy"
                inputProps={{ type: "text", maxLength: 10 }}
                value={dob}
                onChange={(e) => {
                  setDob(e.target.value);
                }}
                onBlur={(e) => {
                  setIsValidDob(isDobValid(e.target.value));
                }}
                error={!isValidDob || dateOfBirthErrorMessage.length !== 0}
                disabled={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                }
                className={
                  selectedPatientId.length > 0 && !props.isDiagnosticOrder
                    ? styles.disabledInput
                    : ""
                }
              />
              {!isValidDob || dateOfBirthErrorMessage.length !== 0 ? (
                <FormHelperText
                  className={
                    !isValidDob || dateOfBirthErrorMessage.length !== 0
                      ? styles.showMessage
                      : styles.hideMessage
                  }
                  component="div"
                  error={true}
                >
                  {dateOfBirthErrorMessage}
                </FormHelperText>
              ) : null}
            </FormControl>
            <FormControl
              className={styles.formControl}
              onClick={
                selectedPatientId.length && !props.isDiagnosticOrder
                  ? () => setIsDisablePopoverOpen(true)
                  : () => {}
              }
            >
              <label>Gender</label>
              <Grid container spacing={2} className={styles.btnGroup}>
                {orderedGenders.map((gender) => {
                  return (
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      className={`${
                        (patientGender &&
                          !isProHealth &&
                          gender !== genderSelected) ||
                        (!props.isDiagnosticOrder &&
                          selectedPatientId.length > 0 &&
                          gender !== genderSelected)
                          ? styles.hideBtn
                          : ""
                      }`}
                    >
                      <AphButton
                        color="secondary"
                        disabled={patientGender && !isProHealth}
                        className={`${styles.genderBtns} ${
                          gender === genderSelected ? styles.btnActive : ""
                        }`}
                        value={genderSelected}
                        onClick={() => {
                          setGenderSelected(gender as Gender);
                        }}
                      >
                        {_startCase(_toLower(gender))}
                      </AphButton>
                    </Grid>
                  );
                })}
              </Grid>
            </FormControl>
          </div>
          <FormControl
            className={`${styles.formControl} ${styles.relationMenu}`}
            fullWidth
          >
            <label>Relation</label>
            <AphSelect
              onChange={(e) => {
                setSelectedRelation(e.target.value as Relation);
                setRelation(e.target.value as Relation);
              }}
              value={relation}
            >
              {orderedRelations.map((relation) => {
                return (
                  <MenuItem
                    classes={{ selected: styles.menuSelected }}
                    value={relation}
                  >
                    {_startCase(_toLower(relation))}
                  </MenuItem>
                );
              })}
            </AphSelect>
            {primaryUserErrorMessage && (
              <FormHelperText component="div" error={true}>
                {primaryUserErrorMessage}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={`${styles.formControl} ${styles.noMargin}`}
            fullWidth
          >
            <AphTextField
              label="Email Address (Optional)"
              placeholder="name@email.com"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                if (e.target.value !== "") {
                  setIsEmailAddressValid(isEmailValid(e.target.value));
                }
              }}
              onBlur={(e) => {
                if (e.target.value !== "") {
                  setIsEmailAddressValid(isEmailValid(e.target.value));
                } else {
                  setIsEmailAddressValid(true);
                }
              }}
              error={!isEmailAddressValid && emailAddress.length > 0}
            />
            {emailAddress !== "" && !isEmailAddressValid ? (
              <FormHelperText
                className={
                  !isEmailAddressValid ? styles.showMessage : styles.hideMessage
                }
                component="div"
                error={true}
              >
                Invalid email id
              </FormHelperText>
            ) : null}
          </FormControl>
        </div>
      </div>
      <div className={styles.dialogActions}>
        <AphButton
          onClick={() => closeHandler(false)}
          disabled={isProfileDelete}
          title={"Cancel"}
        >
          Cancel
        </AphButton>
        <AphButton
          color="primary"
          onClick={async () => {
            const date = parse(dob, "dd/MM/yyyy", new Date());
            const userObject = {
              firstName: firstName,
              lastName: lastName,
              dateOfBirth: format(date, "yyyy-MM-dd"),
              gender: genderSelected,
              relation: relation,
              emailAddress: emailAddress,
              photoUrl: photoUrl,
              id: selectedPatientId,
              mobileNumber: localStorage.getItem("mobile_num") || "",
            };
            setUserInput(userObject);
            if (selectedPatientId.length > 0) {
              setMutationLoading(true);
              if (props.isDiagnosticOrder) {
                diagnosticOrderProfileCreation(userObject);
              } else {
                delete userObject.mobileNumber;
                try {
                  const { data, errors } = await editProfileMutation({
                    variables: {
                      editProfileInput: { ...userObject },
                    },
                  });
                  if (isProHealth) {
                    updateProfileFromProHealth(userObject);
                  }
                  refetchPatients();
                  closeHandler(false);
                  successHandler(true);
                } catch (error) {
                  console.error(
                    "An error occurred while editing profile.",
                    error
                  );
                } finally {
                  setMutationLoading(false);
                }
              }
            } else {
              setIsAddNewProfileConfirmDialogOpen(true);
              eventTracking("Save Member Profile Clicked", {
                ...getPatientAttributes(patientDetails),
                Nav_src: "Home page",
              });
            }
          }}
          disabled={!disableSubmitButton || mutationLoading}
          classes={{
            root: mutationLoading ? "" : styles.saveButton,
            disabled: !disableSubmitButton ? styles.saveBtnDisable : "",
          }}
          title={"save"}
        >
          {mutationLoading ? <CircularProgress size={20} /> : "Save"}
        </AphButton>
        <Popover
          open={isDisablePopoverOpen}
          anchorEl={disableFieldsRef.current}
          onClose={() => setIsDisablePopoverOpen(false)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ root: styles.popOver, paper: styles.popDisablepopover }}
        >
          <h3>Uh oh! :( </h3>
          <h6>You cannot edit Name, Date of Birth and Gender!</h6>
          <AphButton
            className={styles.addnewMemberBtn}
            onClick={() => {
              props.addNewProfileFromEditMenu();
              setIsDisablePopoverOpen(false);
              setFirstName("");
              setLastName("");
              setDob("");
              setGenderSelected("");
              setPhotoUrl("");
              setRelation("");
            }}
          >
            ADD NEW MEMBER
          </AphButton>
        </Popover>
      </div>
      <Alerts
        setAlertMessage={setAlertMessage}
        alertMessage={alertMessage}
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
      />
      <AphDialog open={isAddNewProfileConfirmDialogOpen} maxWidth="sm">
        <AphDialogClose
          onClick={() => {
            setIsAddNewProfileConfirmDialogOpen(false);
            closeHandler(false);
          }}
          title={"Close"}
        />
        <AphDialogTitle className={styles.memberTitle}>
          New Member Details
        </AphDialogTitle>
        <AddNewProfileConfirm
          handleEdit={() => {
            setIsAddNewProfileConfirmDialogOpen(false);
          }}
          successHandler={() => {
            handleProfileSubmit();
          }}
          userInput={userInput}
        />
      </AphDialog>
    </div>
  );
};
